import joinIndicator from './assets/joinindicator.png'
import { toNumber } from "lodash";
import { Extent, createOrUpdateFromCoordinates } from "ol/extent";
import VectorLayer from "ol/layer/Vector";
import { Style, Icon } from "ol/style";
import AppGlobalization from "./AppGlobalization";
import { CheckSubscriptionBySessionIdCommand } from "./CheckSubscriptionBySessionIdCommand";
import CloudSaveHelper from "./CloudSaveHelper";
import CloudSaveReadModel from "./CloudSaveReadModel";
import { Command } from "./Command";
import ConfirmAccountCommand from "./ConfirmAccountCommand";
import CurrentPosition from "./CurrentPosition";
import ErrorHelper from "./ErrorHelper";
import HomeMethods from "./HomeMethods";
import { IGNRenderer } from "./IGNRenderer";
import { MapBoxSatelliteRenderer, MapBoxSatelliteStreetsRenderer, MapBoxDefaultRenderer } from "./MapBoxRenderer";
import { OSMRenderer } from "./OSMRenderer";
import { OSRenderer } from "./OSRenderer";
import RendererKeyHelper from "./RendererKeyHelper";
import { RendererMetadata } from "./RendererMetadata";
import store from "./store";
import { Styles } from "./Styles";
import { UserMessageCommand } from "./UserMessageCommand";
import StorageHelper from './StorageHelper';
import _ from 'lodash';
import GpxGeoJson from './GpxGeoJson';

export default class HomeMounted extends HomeMethods {
    async mounted() {
        let cloudSaveModel = null as CloudSaveReadModel|null
        if (this.$route.params && this.$route.params['prefix'] && this.$route.params['gpxUrl']) {
            try {
                const cloudResponse = await CloudSaveHelper.CloudSaveGet(this.$route.params['prefix'], this.$route.params['gpxUrl'])
                this.loadedTrackGpxUrl = this.$route.params['gpxUrl']
                if(!store.getters.isSubscribed) {
                    this.operation = 0 // make sure they're in pan mode, because free users can't edit a shared track
                }
                cloudSaveModel = cloudResponse.data
                this.loadedTrackOwnedByCurrentUser = cloudSaveModel.ownedByCurrentUser
                this.loadedTrackIsPublic = cloudSaveModel.public
            } catch(error) {
                console.error(error)
                ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ?? this.showError('Could not load the specified GPX (you may need to login first if it is private)')
                this.$router.push('/')
            }
        } else {
            this.loadedTrackOwnedByCurrentUser = null
            this.loadedTrackGpxUrl = null
            this.loadedTrackIsPublic = null
        }

        document.addEventListener('keydown', (event) => {
            if(event.key === "z" && event.ctrlKey) {
                const path = event.composedPath()
                const isInInputOrDialog = _.some(path, t => {
                    return t instanceof HTMLElement && (t.tagName.toLowerCase() === 'input' || t.classList.contains('v-dialog__content'))
                })
                if(!isInInputOrDialog) {
                    this.undo()
                }
            }
            if(event.key === "t" && event.ctrlKey && event.altKey) { 
                // Ctrl+Alt+T => "easter egg" : animate a traveller along the current track
                this.showTraveller()
            }
        });

        document.addEventListener('selectionchange', (ev) =>  {
            const sel = getSelection()
            if(sel) {
                const anchor = sel.anchorNode as HTMLElement
                if(anchor && anchor.classList && anchor.classList.contains('ol-unselectable')) {
                    sel.removeAllRanges()
                }
            }
        })

        function eventTargetIsInside(eventTarget: EventTarget|null, element: HTMLElement|null) {
            return eventTarget instanceof Element && element !== null && element.contains(eventTarget);
        }

        const map = document.getElementById('map');
        const placesearch = document.getElementById('placesearch');
        document.addEventListener('dblclick', ev => {
            if (!eventTargetIsInside(ev.target, map)) {
                ev.preventDefault();
            }
        })
        document.addEventListener('touchstart click', function(ev) {
            if (eventTargetIsInside(ev.target, placesearch)) {
                ev.preventDefault();
            }
        })

        const storedDistanceUnit = StorageHelper.getItem('DistanceUnit')
        if(storedDistanceUnit) {
            this.distanceUnit = storedDistanceUnit
        } else {
            this.distanceUnit = AppGlobalization?.DistanceUnit ?? "mi";
        }

        const elevationChartPaneSize = StorageHelper.getItem('elevationChartPaneSize')
        if(elevationChartPaneSize !== null) {
            this.elevationChartPaneSize = toNumber(elevationChartPaneSize)
        }

        const storedTrackpointLimit = StorageHelper.getItem('trackpointlimit')
        if(storedTrackpointLimit !== null) {
            this.trackPointLimit = toNumber(storedTrackpointLimit)
        } else {
            this.trackPointLimit = null
        }
        
        let extent: Extent|undefined = undefined
        if(cloudSaveModel) {
            extent = createOrUpdateFromCoordinates([cloudSaveModel.bottomLeft, cloudSaveModel.topRight])
        } else {
            const currentPositionString = StorageHelper.getItem("CurrentPosition")
            if(currentPositionString) {
                const currentPosition: CurrentPosition = JSON.parse(currentPositionString)
                if(currentPosition.center && currentPosition.extent) {
                    extent = currentPosition.extent
                }
            }
        }

        const extentToUse = extent ?? await this.getDefaultExtent() // default extent

        const renderersToInitialize: RendererMetadata<any>[] = []
        
        const rendererKeys = RendererKeyHelper.getRendererKeys()
        renderersToInitialize.push(new RendererMetadata("OpenStreetMap", OSMRenderer.type, (type: string) => new OSMRenderer(type, "map", extentToUse), OSMRenderer.IsValidForExtent, false))
        this.addRendererIfKeyPresent(rendererKeys, renderersToInitialize, 
            "mapbox", k => new RendererMetadata("Map", "mapbox", (type: string) => new MapBoxDefaultRenderer(type, "map", extentToUse, k), MapBoxDefaultRenderer.IsValidForExtent, true))
        this.addRendererIfKeyPresent(rendererKeys, renderersToInitialize,
            "mapbox", k => new RendererMetadata("Map (satellite)", "mapbox-satellite", (type: string) => new MapBoxSatelliteRenderer(type, "map", extentToUse, k), MapBoxSatelliteRenderer.IsValidForExtent, true))
        this.addRendererIfKeyPresent(rendererKeys, renderersToInitialize,
            "mapbox", k => new RendererMetadata("Map (satellite/streets)", "mapbox-satellite-streets", (type: string) => new MapBoxSatelliteStreetsRenderer(type, "map", extentToUse, k), MapBoxSatelliteStreetsRenderer.IsValidForExtent, true))
        this.addRendererIfKeyPresent(rendererKeys, renderersToInitialize, 
            "ign", k => new RendererMetadata("IGN (France)", "ign", (type: string) => new IGNRenderer(type, "map", extentToUse, k), IGNRenderer.IsValidForExtent, true))
        this.addRendererIfKeyPresent(rendererKeys, renderersToInitialize, 
            "os", k => new RendererMetadata("Ordnance Survey (GB)", "os", (type: string) => new OSRenderer(type, "map", extentToUse, k), OSRenderer.IsValidForExtent, true))
        
        this.renderers = renderersToInitialize
        this.validRenderers = this.renderers.filter(r => r.isValidForExtent(extentToUse))
        
        const rendererName = cloudSaveModel?.renderer ?? StorageHelper.getItem("Renderer") // get the renderer they had last
        let rendererIndex = this.validRenderers.findIndex(r => r.value === rendererName && (
            (cloudSaveModel && cloudSaveModel.renderer === r.value) // allow any (free) user viewing a saved track to view the renderer the subscriber saved it with
             || this.$store.getters.isOrMightBeSubscribed || !r.isSubscriberOnly))
        if(rendererIndex === -1) {
            rendererIndex = 0
        }

        this.renderer = this.validRenderers[rendererIndex].value
        this.currentRenderer = this.validRenderers[rendererIndex].creator(this.validRenderers[rendererIndex].value)

        this.setupRendererOnInitOrChange(false) // already specified the extent above

        this.currentRenderer.map.addLayer(new VectorLayer({ //not a track vector layer, as we don't want to remove it on undo
            source: this.routingLineFeatureSource,
            style: Styles.ThickTransparentLightBlue
        }))

        this.currentRenderer.map.addLayer(new VectorLayer({ //not a track vector layer, as we don't want to remove it on undo
            source: this.joinIndicatorFeatureSource,
            style: new Style({
                image: new Icon({
                    src: joinIndicator,
                })
            })
        }))
        this.addWaypointLayerToMap()
        this.addAuxiliaryMarkersLayerToMap()
        this.addElevationHoverIndicatorLayerToMap()

        if(cloudSaveModel && cloudSaveModel.gpx.tracks.length) {
            this.addTracksFromGpxGeoJson(cloudSaveModel.gpx)
            this.addWaypointsFromGpxGeoJson(cloudSaveModel.gpx)
        }
        if(this.tracks.length === 0) {
            this.addNewTrack();
        } else {
            this.operation = 0 // don't want to enter drawing mode on loading gpx from cloud
        }

        const commands: Command[] = await this.$store.getters.userCommands
        commands.forEach(async command => {
            let handled = true
            if(command instanceof UserMessageCommand) {
                await this.handleUserMessageCommand(command)
            }
            else if (command instanceof CheckSubscriptionBySessionIdCommand) {
                await this.handleCheckSubscriptionBySessionIdCommand(command)
            }
            else if (command instanceof ConfirmAccountCommand) {
                await this.handleConfirmAccountCommand(command)
            }
            else {
                handled = false
            }
            if(handled) {
                store.commit('handledCommand', command.id)
            }
        })

        if (StorageHelper.getItem("DontShowWelcome") !== "true") {
            this.dialog = 'welcome'
        }

        const sidebarSetting = StorageHelper.getItem('showSidebar');
        const pricingSidebarSetting = StorageHelper.getItem('showingPricingSidebar');
        setTimeout(() => { // works better in dev with this. Doesn't do any harm, and although haven't come across them yet, may be some situations in prod where it works better too.
            this.sidebar = sidebarSetting === 'true'
            this.homeIsShowingPricingSidebar = pricingSidebarSetting === 'true';
        })

        this.mergeSegmentsOnSaveToFile = StorageHelper.getItem('mergeSegmentsOnSaveToFile') !== 'false' // true being the default
        
        // fix for file upload on safari
        const inputs = document.querySelectorAll('.v-file-input input');
        inputs.forEach((input) => {
            input.remove()
        })

        try {
            // try and load saved gpx
            if(this.noContent()) {
                const storedGpx = StorageHelper.getItem('gpx');
                if(storedGpx) {
                    const gpx: GpxGeoJson = JSON.parse(storedGpx);
                    this.addTracksFromGpxGeoJson(gpx);
                    this.addWaypointsFromGpxGeoJson(gpx);
                    this.operation = 0;
                }
            }
        } catch(error) {
            console.error('error loading saved gpx: ', error)
        }
        setTimeout(() => {
            this.initComplete = true;
        }, 0)       
    }
}